import { Global } from "@emotion/react";
import styled from "@emotion/styled";
import React, { useState } from "react";

import { navExperiment } from "@brexhq/next/src/config/experimentation/data";
import { getNavById } from "@brexhq/next/src/data/dataUtils";
import {
  ExperimentAnalyticsData,
  useTrackStatsigIfExperiment,
} from "@brexhq/next/src/hooks/analytics/useTrackStatsig";
import useIsomorphicLayoutEffect from "@brexhq/next/src/hooks/useIsomorphicLayoutEffect";
import { statsigClientSide } from "@brexhq/next/src/utils/experimentation/statsig";

import { OneClickSignInDialogProps } from "../modules/OneClickSignIn/OneClickSignInDialog";
import { useOneClickSignIn } from "../modules/OneClickSignIn/useOneClickSignIn";

import { NAV_HEIGHT } from "./consts";
import DesktopNav from "./desktop";
import MobileNav from "./mobile";
import type { INavigation, INavigationProps } from "./types";

const createStyledComponents = (mobileBreakpoint: "SML" | "MED" = "SML") => {
  const Desktop = styled(DesktopNav)(({ theme }) => ({
    [theme.breakpoints.nav.SML]: {
      display: "none",
    },
    ...(mobileBreakpoint === "MED" && {
      [theme.breakpoints.nav.MED]: {
        display: "none",
      },
    }),
  }));

  const Mobile = styled(MobileNav)(({ theme }) => ({
    display: "none",
    ...(mobileBreakpoint === "SML" && {
      [theme.breakpoints.nav.SML]: {
        display: "unset",
      },
    }),
    ...(mobileBreakpoint === "MED" && {
      [theme.breakpoints.nav.SML]: {
        display: "unset",
      },
      [theme.breakpoints.nav.MED]: {
        display: "unset",
      },
    }),
  }));

  return { Desktop, Mobile };
};

/**
 * Navigation component with no experiments -- use for previews only.
 */
export const NavigationComponent = (
  props: INavigationProps & {
    loading?: boolean;
    oneClickSignInDialogProps?: OneClickSignInDialogProps;
  },
) => {
  const { loading, data, ...rest } = props;
  const mobileBreakpoint = data?.mobileBreakpoint || "SML";
  const { Desktop, Mobile } = createStyledComponents(mobileBreakpoint);

  return (
    <>
      <Desktop loading={loading} data={data} {...rest} />
      <Mobile loading={loading} data={data} {...rest} />
      <Global
        styles={{
          "html, body": {
            "--brex-nav-height": `${NAV_HEIGHT}px`,
            "--brex-sticky-padding": "24px",
            "--brex-base-scroll-padding":
              "calc(var(--brex-nav-height) + var(--brex-sticky-padding))",
            "--brex-scroll-padding": "var(--brex-base-scroll-padding)",
            scrollPaddingTop: "var(--brex-scroll-padding)",
          },
        }}
      />
    </>
  );
};

export default function Navigation(props: INavigationProps) {
  const { data, ...rest } = props;

  // If we have a nav experiment defined, we need to wait for the experiment to bucket the user before showing the nav
  const [isNavLoading, setIsNavLoading] = useState(Boolean(navExperiment));
  const [navData, setNavData] = React.useState<INavigation>(data);
  const [analyticsData, setAnalyticsData] =
    React.useState<ExperimentAnalyticsData | null>(null);

  // Need to manage props here because the OneClickSignInDialog must be mounted in both Mobile and Desktop Navs
  // In order to prevent multiple instances of the hook running, (ie: tracking multiple events for redirect and gate fetches) we need to manage the props here
  const oneClickSignInDialogProps = useOneClickSignIn();

  useIsomorphicLayoutEffect(() => {
    const getNavExp = async () => {
      if (!navExperiment) return;

      const {
        statsigExperimentId,
        references,
        parameterKey,
        defaultParameterValue,
        experimentStatus,
      } = navExperiment;
      const { value } = await statsigClientSide({
        statsigExperimentId,
        parameterKey: parameterKey,
        defaultParameterValue: defaultParameterValue,
      });

      const nav = references[value];

      setIsNavLoading(false);
      setNavData(getNavById(nav.docId));
      setAnalyticsData({
        experimentId: statsigExperimentId,
        experimentType: "nav",
        variation: nav.variation,
        experimentStatus,
      });
    };

    getNavExp();
  }, []);

  // Will only track if inside an experiment
  useTrackStatsigIfExperiment(analyticsData);
  return (
    <NavigationComponent
      loading={isNavLoading}
      data={navData}
      oneClickSignInDialogProps={oneClickSignInDialogProps}
      {...rest}
    />
  );
}
